/**
 * @file Eclairage de scene du jeu (ambiance + soleil), normalise pour three >= r155 ; partage entre GameRenderer et la galerie de dev.
 */
import { AmbientLight, Color, DirectionalLight } from 'three';
import type { Scene } from 'three';
import { RENDER } from '@config/index';

/** Luminosite de la face eclairee (multiple de la couleur du materiau) : pastel lumineux sans surexposition. */
export const LIT_EXPOSURE = 1.1;

/**
 * Ajoute a `scene` la lumiere ambiante et le soleil du jeu. three >= r155 divise l'eclairement par PI : on normalise les intensites de
 * la config pour que la face eclairee rende LIT_EXPOSURE x l'albedo (la config n'est pas modifiee).
 */
export const addGameLights = (scene: Scene): { ambient: AmbientLight; sun: DirectionalLight } => {
  const gain = (Math.PI * LIT_EXPOSURE) / (RENDER.lighting.ambient + RENDER.lighting.sunIntensity);
  // ambiance teintee (violet nuit par defaut) : les faces a l'ombre se creusent au lieu de griser
  const ambientColor = new Color(0xffffff).lerp(new Color(RENDER.toon.shadowTint), RENDER.lighting.ambientTintMix);
  const ambient = new AmbientLight(ambientColor, RENDER.lighting.ambient * gain);
  const sun = new DirectionalLight(0xffffff, RENDER.lighting.sunIntensity * gain);
  sun.position.set(...RENDER.lighting.sunDir);
  scene.add(ambient, sun);
  return { ambient, sun };
};

/**
 * Etalonnage par decor (RENDER.lighting.grade[key]) : teinte du soleil et des ombres ; cle inconnue = eclairage neutre d'origine.
 * Ne touche que la couleur des lumieres (aucun cout GPU).
 */
export const applyLightGrade = (lights: { ambient: AmbientLight; sun: DirectionalLight }, key: string): void => {
  const g = RENDER.lighting.grade?.[key];
  lights.ambient.color.set(0xffffff).lerp(new Color(g?.shade ?? RENDER.toon.shadowTint), RENDER.lighting.ambientTintMix);
  lights.sun.color.set(g?.sun ?? 0xffffff);
};
